// types/payment.ts
export interface PermitSubscription {
    PermitID: number;
    PermitName: string;
    Period: string;
    Price: number;
  }
  
  export interface PaymentAcknowledgementProps {
    showPaymentAcknowledgement: boolean;
    applicationDetails: PermitSubscription[];
    totalAmountPayable: number;
    totalAmountWithoutTax: number;
    totalTaxAmount: number;
    taxRate
    onRelogin: () => void;
  }
  
  // components/PaymentAcknowledgement.tsx
  import React from 'react';
  import {
    Dialog,
    DialogContent,
    Typography,
    Box,
    Paper,
    Button,
    Divider,
    Grid,
    useTheme,
    useMediaQuery,
    Alert
  } from '@mui/material';
  import { 
    Receipt, 
    LoginOutlined 
  } from '@mui/icons-material';
  import CheckCircleOutlineTwoToneIcon from '@mui/icons-material/CheckCircleOutlineTwoTone';
  
  const SubscriptionPaymentAcknowledgement: React.FC<PaymentAcknowledgementProps> = ({
    showPaymentAcknowledgement,
    applicationDetails,
    totalAmountPayable,
    totalAmountWithoutTax,
    totalTaxAmount,
    taxRate,
    onRelogin
  }) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
    const formatCurrency = (amount: number) => {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD'
      }).format(amount);
    };
  
    const renderPermitSubscription = (permit: PermitSubscription) => (
      <Box sx={{ 
        p: 2, 
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 1,
        mb: 1,
        '&:last-child': { mb: 0 },
        '&:hover': {
          bgcolor: 'rgba(0, 0, 0, 0.01)'
        }
      }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <Box>
            <Typography variant="body1" fontWeight="medium">
              {permit.PermitName}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              {permit.Period} Plan • Permit ID: {permit.PermitID}
            </Typography>
          </Box>
          <Typography variant="body1" fontWeight="medium">
            {formatCurrency(permit.Price)}
          </Typography>
        </Box>
      </Box>
    );
  
    return (
      <Dialog
        open={showPaymentAcknowledgement}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            mx: isMobile ? 2 : 0
          }
        }}
      >
        <DialogContent sx={{ p: 0 }}>
          <Box 
            sx={{ 
              bgcolor: 'primary.main', 
              color: 'white',
              p: 3,
              textAlign: 'center'
            }}
          >
            <CheckCircleOutlineTwoToneIcon sx={{ fontSize: 48, mb: 1, color: 'green' }} />
            <Typography variant="h4" gutterBottom fontWeight="bold">
              Payment Successful!
            </Typography>
            <Typography variant="subtitle1" color='#ffffff'>
              Your subscriptions have been processed successfully
            </Typography>
          </Box>
  
          <Box sx={{ p: 3 }}>
            <Alert 
              severity="info" 
              sx={{ mb: 3 }}
              icon={<LoginOutlined />}
            >
              Please re-login to access your newly subscribed permits
            </Alert>
  
            <Paper 
              elevation={0} 
              sx={{ 
                p: 3, 
                bgcolor: 'grey.50',
                borderRadius: 2
              }}
            >
              <Grid container spacing={2}>
                
                <Grid item xs={12}>
                  <Divider />
                </Grid>
  
                <Grid item xs={12}>
                  <Typography variant="h6" gutterBottom sx={{ color: 'primary.main' }}>
                    Subscription Details
                  </Typography>
                  <Box sx={{ mb: 2 }}>
                    {applicationDetails?.map((permit) => (
                      <React.Fragment key={permit.PermitID}>
                        {renderPermitSubscription(permit)}
                      </React.Fragment>
                    ))}
                  </Box>
                </Grid>
  
                <Grid item xs={12}>
                  <Divider />
                </Grid>
  
                <Grid item xs={12}>
                  <Typography variant="h6" gutterBottom sx={{ color: 'primary.main' }}>
                    Payment Summary
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="body2" color="text.secondary">
                        Subtotal
                      </Typography>
                      <Typography variant="body2">
                        {formatCurrency(totalAmountWithoutTax)}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="body2" color="text.secondary">
                        Tax ({taxRate}%)
                      </Typography>
                      <Typography variant="body2">
                        {formatCurrency(totalTaxAmount)}
                      </Typography>
                    </Box>
                    <Divider sx={{ my: 1 }} />
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="subtitle1" fontWeight="bold">
                        Total Paid
                      </Typography>
                      <Typography variant="subtitle1" fontWeight="bold" color="primary">
                        {formatCurrency(totalAmountPayable)}
                      </Typography>
                    </Box>
                  </Box>
                </Grid>
              </Grid>
            </Paper>
  
            <Box sx={{ 
              mt: 3, 
              display: 'flex', 
              flexDirection: { xs: 'column', sm: 'row' },
              gap: 2, 
              justifyContent: 'center' 
            }}>
              <Button
                variant="contained"
                color="primary"
                onClick={onRelogin}
                startIcon={<LoginOutlined />}
                fullWidth={isMobile}
                sx={{ 
                  textTransform: 'none',
                  minWidth: 120,
                  py: 1
                }}
              >
                Re-login Now
              </Button>
              <Button
                variant="outlined"
                startIcon={<Receipt />}
                onClick={() => window.print()}
                fullWidth={isMobile}
                sx={{ 
                  textTransform: 'none',
                  minWidth: 120,
                  py: 1
                }}
              >
                Download Invoice
              </Button>
            </Box>
          </Box>
        </DialogContent>
      </Dialog>
    );
  };
  
  export default SubscriptionPaymentAcknowledgement;